import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../../stores';
import {
  DISCLAIMER_LANGUAGES,
  DISCLAIMER_TYPE_LABELS,
  DISCLAIMER_TYPES,
  type CreateDisclaimerRequest,
  type Disclaimer,
  type DisclaimerTypeKey,
} from '../../types';
import './Admin.css';

interface FormState {
  typeKey: DisclaimerTypeKey;
  language: string;
  title: string;
  content: string;
  isActive: boolean;
}

const defaultForm: FormState = {
  typeKey: 'terms',
  language: 'en',
  title: '',
  content: '',
  isActive: true,
};

function truncate(str: string, max: number): string {
  if (str.length <= max) return str;
  return `${str.slice(0, max)}…`;
}

function typeLabel(key: string): string {
  return DISCLAIMER_TYPE_LABELS[key as DisclaimerTypeKey] ?? key;
}

export function Disclaimers() {
  const { accessToken } = useAuthStore();
  const [rows, setRows] = useState<Disclaimer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Disclaimer | null>(null);
  const [formData, setFormData] = useState<FormState>(defaultForm);

  const fetchRows = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/disclaimers', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Disclaimer listesi alınamadı');
      const data = (await response.json()) as Disclaimer[];
      setRows(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Yükleme hatası');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleOpenModal = (row?: Disclaimer) => {
    setError('');
    if (row) {
      setEditing(row);
      setFormData({
        typeKey: (DISCLAIMER_TYPES.includes(row.typeKey as DisclaimerTypeKey)
          ? row.typeKey
          : 'terms') as DisclaimerTypeKey,
        language: row.language,
        title: row.title,
        content: row.content,
        isActive: row.isActive,
      });
    } else {
      setEditing(null);
      setFormData(defaultForm);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditing(null);
    setFormData(defaultForm);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setActionLoading(editing?.id ?? -1);

    const body: CreateDisclaimerRequest = {
      typeKey: formData.typeKey,
      language: formData.language,
      title: formData.title.trim(),
      content: formData.content,
      isActive: formData.isActive,
    };

    try {
      const url = editing ? `/api/admin/disclaimers/${editing.id}` : '/api/admin/disclaimers';
      const method = editing ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const data = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(data.message || 'Kayıt başarısız');
      }

      handleCloseModal();
      await fetchRows();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kayıt başarısız');
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggle = async (row: Disclaimer) => {
    setActionLoading(row.id);
    setError('');
    try {
      const response = await fetch(`/api/admin/disclaimers/${row.id}/toggle`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Durum değiştirilemedi');
      await fetchRows();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Durum değiştirilemedi');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (row: Disclaimer) => {
    if (!confirm(`"${row.title}" kaydını silmek istediğinize emin misiniz?`)) return;
    setActionLoading(row.id);
    setError('');
    try {
      const response = await fetch(`/api/admin/disclaimers/${row.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) {
        const data = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(data.message || 'Silinemedi');
      }
      await fetchRows();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Silinemedi');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return <div className="admin-loading">Yükleniyor...</div>;
  }

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <div>
          <h1>Disclaimers</h1>
          <p>Yasal ve bilgilendirme metinlerini tip ve dil bazında yönetin.</p>
        </div>
        <button className="admin-btn admin-btn-primary" onClick={() => handleOpenModal()}>
          + Yeni Disclaimer
        </button>
      </header>

      {error && <div className="admin-error">{error}</div>}

      <section className="admin-table-section">
        <div className="admin-table-toolbar">
          <h2>Disclaimers ({rows.length})</h2>
        </div>

        {rows.length === 0 ? (
          <div className="admin-empty-state">
            <p>Henüz disclaimer yok.</p>
            <button className="admin-btn admin-btn-primary" onClick={() => handleOpenModal()}>
              İlk kaydı ekle
            </button>
          </div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Tip</th>
                  <th>Dil</th>
                  <th>Başlık</th>
                  <th>İçerik</th>
                  <th>Durum</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <span className="provider-type-badge">{typeLabel(row.typeKey)}</span>
                    </td>
                    <td>
                      <code className="admin-code-inline">{row.language}</code>
                    </td>
                    <td>
                      <span className="provider-name">{row.title}</span>
                    </td>
                    <td>
                      <span className="provider-base-url" title={row.content}>
                        {truncate(row.content, 64)}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className={`admin-toggle-btn ${row.isActive ? 'active' : ''}`}
                        onClick={() => handleToggle(row)}
                        disabled={actionLoading === row.id}
                        title={row.isActive ? 'Deaktif Et' : 'Aktif Et'}
                      >
                        {row.isActive ? '✓ Aktif' : '✗ Pasif'}
                      </button>
                    </td>
                    <td>
                      <div className="admin-action-buttons">
                        <button
                          type="button"
                          className="admin-edit-btn"
                          onClick={() => handleOpenModal(row)}
                          disabled={actionLoading === row.id}
                          title="Düzenle"
                        >
                          ✏️
                        </button>
                        <button
                          type="button"
                          className="admin-delete-btn"
                          onClick={() => handleDelete(row)}
                          disabled={actionLoading === row.id}
                          title="Sil"
                        >
                          {actionLoading === row.id ? '...' : '🗑'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showModal && (
        <div className="admin-modal-overlay" onClick={handleCloseModal}>
          <div className="admin-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editing ? 'Disclaimer düzenle' : 'Yeni disclaimer'}</h2>
              <button type="button" className="admin-modal-close" onClick={handleCloseModal}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="admin-form">
              <div className="admin-form-group">
                <label className="admin-form-label">
                  Tip *
                  <select
                    className="admin-form-select"
                    value={formData.typeKey}
                    onChange={(e) =>
                      setFormData({ ...formData, typeKey: e.target.value as DisclaimerTypeKey })
                    }
                    required
                  >
                    {DISCLAIMER_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {DISCLAIMER_TYPE_LABELS[t]}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">
                  Dil *
                  <select
                    className="admin-form-select"
                    value={formData.language}
                    onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                    required
                  >
                    {DISCLAIMER_LANGUAGES.map((l) => (
                      <option key={l.value} value={l.value}>
                        {l.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">
                  Başlık *
                  <input
                    type="text"
                    className="admin-form-input"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    maxLength={256}
                    placeholder="Örn: Terms of Service"
                  />
                </label>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">
                  İçerik *
                  <textarea
                    className="admin-form-textarea"
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    required
                    rows={12}
                    placeholder="Metin içeriği…"
                  />
                </label>
              </div>

              <div className="admin-form-group">
                <label className="admin-setting-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  <span>Aktif</span>
                </label>
              </div>

              <div className="admin-form-actions">
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary"
                  onClick={handleCloseModal}
                  disabled={actionLoading !== null}
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="admin-btn admin-btn-primary"
                  disabled={actionLoading !== null}
                >
                  {actionLoading !== null ? 'Kaydediliyor…' : editing ? 'Güncelle' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
